import * as mui from '@mui/material'
import * as React from 'react'
import * as ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { userContext, useUserContext } from 'context/user'
import { langSwitchContext, useLangSwitch, translationsContext, useTranslations } from 'context/translations'

import { theme } from './theme'
import { router } from './routes'


const PASSWORD_FIELDS = ['password', 'password2']

function scrubPasswords(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrubPasswords)
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = PASSWORD_FIELDS.includes(k) ? '[Filtered]' : scrubPasswords(v)
    }
    return result
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      const scrubbed = scrubPasswords(parsed)
      if (scrubbed !== parsed) return JSON.stringify(scrubbed)
    } catch {}
  }
  return value
}

if (window.GLOBAL_CONFIG.SENTRY_DSN) {
  Sentry.init({
    dsn: window.GLOBAL_CONFIG.SENTRY_DSN,
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [/^\//],
      }),
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    environment: window.GLOBAL_CONFIG.ENV,
    beforeSend(event) {
      event.tags = {...(event.tags || {}), route: window.location.pathname}
      if (event.request?.data !== undefined) {
        event.request.data = scrubPasswords(event.request.data)
      }
      if (event.contexts) {
        event.contexts = scrubPasswords(event.contexts) as typeof event.contexts
      }
      return event
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = scrubPasswords(breadcrumb.data) as typeof breadcrumb.data
      }
      return breadcrumb
    },
  })
}

function SentryContextSync() {
  const user = React.useContext(userContext)
  const [lang] = React.useContext(langSwitchContext)
  React.useEffect(() => {
    if (!user.data) {
      return
    }
    Sentry.setUser({email: user.data.email})
    Sentry.setContext('userData', user.data)
  }, [user.data])
  React.useEffect(() => {
    Sentry.setTag('language', lang)
  }, [lang])
  return null
}

function LangSwitchProvider(props: {children: React.ReactNode}) {
  const langSwitchCb = useLangSwitch()
  return (
    <langSwitchContext.Provider value={langSwitchCb}>
      {props.children}
    </langSwitchContext.Provider>
  )
}

function TranslationsProvider(props: {children: React.ReactNode}) {
  const translationsCb = useTranslations()
  return (
    <translationsContext.Provider value={translationsCb}>
      {props.children}
    </translationsContext.Provider>
  )
}

function RootComponent() {
  const user = useUserContext()

  return (
    <React.StrictMode>
      <mui.CssBaseline/>
      <mui.ThemeProvider theme={theme}>
        <userContext.Provider value={user}>
          <LangSwitchProvider>
            <SentryContextSync/>
            <TranslationsProvider>
              <RouterProvider router={router}/>
            </TranslationsProvider>
          </LangSwitchProvider>
        </userContext.Provider>
      </mui.ThemeProvider>
    </React.StrictMode>
  )
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

root.render(<RootComponent />)
